import { BLUR_PX, OVERBLEED, SCALE } from './constants';
import { bindPointerEvents } from './events';
import { createPoints, stepMotion } from './motion';

export const startCanvas2D = (canvas, mouseRef, scrollRef, paletteRef, animationRef) => {
  const ctx = canvas.getContext('2d', { alpha: true, willReadFrequently: false });
  if (!ctx) return () => {};

  canvas.style.filter = `blur(${BLUR_PX}px)`;

  let renderWidth = 0;
  let renderHeight = 0;
  let fullWidth = 0;
  let fullHeight = 0;
  let offscreen = document.createElement('canvas');
  let offCtx = offscreen.getContext('2d', { alpha: true, willReadFrequently: false });
  let pendingResize = false;
  const points = createPoints();
  const bayerMatrix = [
    [0, 32, 8, 40, 2, 34, 10, 42],
    [48, 16, 56, 24, 50, 18, 58, 26],
    [12, 44, 4, 36, 14, 46, 6, 38],
    [60, 28, 52, 20, 62, 30, 54, 22],
    [3, 35, 11, 43, 1, 33, 9, 41],
    [51, 19, 59, 27, 49, 17, 57, 25],
    [15, 47, 7, 39, 13, 45, 5, 37],
    [63, 31, 55, 23, 61, 29, 53, 21],
  ];

  const applySize = () => {
    fullWidth = window.innerWidth + OVERBLEED;
    fullHeight = window.innerHeight + OVERBLEED;
    renderWidth = Math.round(fullWidth * SCALE);
    renderHeight = Math.round(fullHeight * SCALE);
    offscreen.width = renderWidth;
    offscreen.height = renderHeight;
    canvas.style.width = `${fullWidth}px`;
    canvas.style.height = `${fullHeight}px`;
  };

  applySize();
  canvas.width = renderWidth;
  canvas.height = renderHeight;
  const unbind = bindPointerEvents(mouseRef, scrollRef, () => ({ fullWidth, fullHeight }));

  let frameCount = 0;
  const animate = () => {
    frameCount += 1;
    if (frameCount % 2 !== 0) {
      animationRef.current = requestAnimationFrame(animate);
      return;
    }
    if (pendingResize) {
      pendingResize = false;
      applySize();
    }
    const rw = renderWidth;
    const rh = renderHeight;
    if (rw === 0 || rh === 0) {
      animationRef.current = requestAnimationFrame(animate);
      return;
    }

    stepMotion(points, mouseRef.current, scrollRef.current, fullHeight, paletteRef.current);

    const imageData = offCtx.createImageData(rw, rh);
    const data = imageData.data;
    const minDim = Math.min(rw, rh);
    for (let y = 0; y < rh; y++) {
      for (let x = 0; x < rw; x++) {
        const idx = (y * rw + x) * 4;
        let r = 0;
        let g = 0;
        let b = 0;
        let totalWeight = 0;
        for (let p = 0; p < points.length; p++) {
          const point = points[p];
          const ddx = x - point.x * rw;
          const ddy = y - point.y * rh;
          const radiusPx = point.radius * minDim;
          const normalizedDist = Math.sqrt(ddx * ddx + ddy * ddy) / radiusPx;
          if (normalizedDist < 1.5) {
            const t = Math.max(0, 1 - normalizedDist);
            const weight = t * t * (3 - 2 * t);
            const rgb = paletteRef.current[p];
            r += rgb[0] * weight;
            g += rgb[1] * weight;
            b += rgb[2] * weight;
            totalWeight += weight;
          }
        }
        if (totalWeight > 0) {
          const bayerValue = (bayerMatrix[y % 8][x % 8] / 64.0 - 0.5) * 20;
          data[idx] = Math.max(0, Math.min(255, r / totalWeight + bayerValue));
          data[idx + 1] = Math.max(0, Math.min(255, g / totalWeight + bayerValue));
          data[idx + 2] = Math.max(0, Math.min(255, b / totalWeight + bayerValue));
          data[idx + 3] = Math.min(255, totalWeight * 80);
        }
      }
    }

    offCtx.putImageData(imageData, 0, 0);
    if (canvas.width !== rw || canvas.height !== rh) {
      canvas.width = rw;
      canvas.height = rh;
    }
    ctx.clearRect(0, 0, rw, rh);
    ctx.drawImage(offscreen, 0, 0);
    animationRef.current = requestAnimationFrame(animate);
  };

  const onResize = () => {
    pendingResize = true;
  };
  window.addEventListener('resize', onResize);
  animate();

  return () => {
    window.removeEventListener('resize', onResize);
    unbind();
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    offscreen = null;
    offCtx = null;
  };
};
